
import React from 'react';
import { Shield, Radio, Code, Wrench } from 'lucide-react';
import ServiceCard from '../components/services/ServiceCard';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';

const servicesData = [
  {
    id: 'reseaux-securite',
    icon: Shield,
    title: 'Réseaux et Sécurité',
    description: 'Solutions de sécurité complètes pour protéger votre infrastructure IT.',
    features: [
      'Pare-feu nouvelle génération',
      'Solutions VPN sécurisées',
      'Systèmes IDS/IPS',
      'Supervision réseau 24/7',
      'Audit de sécurité'
    ],
    imageSrc: '/images/securiter.webp'
  },
  {
    id: 'telecommunications',
    icon: Radio,
    title: 'Télécommunications',
    description: 'Infrastructure de communication moderne et performante.',
    features: [
      'Téléphonie VoIP',
      'Solutions VPN MPLS',
      'Câblage structuré',
      'Communications unifiées',
      'Visioconférence'
    ],
    imageSrc: '/images/telecom.webp'
  },
  {
    id: 'developpement',
    icon: Code,
    title: 'Développement Web & Mobile',
    description: 'Applications sur mesure pour digitaliser votre activité.',
    features: [
      'Sites web responsifs',
      'Applications mobiles',
      'Portails entreprise',
      'E-commerce',
      'APIs REST'
    ],
    imageSrc: '/images/dev.jpeg'
  },
  {
    id: 'maintenance',
    icon: Wrench,
    title: 'Maintenance Informatique',
    description: 'Support technique et maintenance préventive.',
    features: [
      'Maintenance serveurs',
      'Support utilisateurs',
      'Monitoring proactif',
      'Sauvegardes automatisées',
      'Mises à jour sécurité'
    ],
    imageSrc: '/images/maintenace.webp'
  }
];

const Services = () => {
  return (
    <div className="relative">
      <Navbar />
      
      {/* Image de fond */}
      <div className="relative h-[75vh] min-h-[600px] overflow-hidden">
        <motion.div
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0 z-0"
        >
          <img
            src="/images/w.jpg"
            alt="Services Background"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </motion.div>
      </div>

      {/* Barre de navigation des sections */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-slate-800 to-gray-900 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <nav>
            <ul className="flex justify-center space-x-8">
              <li>
                <a 
                  href="#reseaux-securite" 
                  className="text-white hover:text-blue-400 transition-colors text-lg font-medium"
                >
                  Réseaux & Sécurité
                </a>
              </li>
              <li>
                <a 
                  href="#telecommunications" 
                  className="text-white hover:text-blue-400 transition-colors text-lg font-medium"
                >
                  Télécommunications
                </a>
              </li>
              <li>
                <a 
                  href="#developpement" 
                  className="text-white hover:text-blue-400 transition-colors text-lg font-medium"
                >
                  Développement
                </a>
              </li>
              <li>
                <a 
                  href="#maintenance" 
                  className="text-white hover:text-blue-400 transition-colors text-lg font-medium"
                >
                  Maintenance
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Services Introduction avec arrière-plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16 relative py-16"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50">
              <div className="absolute inset-0 bg-[linear-gradient(45deg,#f8fafc_25%,transparent_25%,transparent_75%,#f8fafc_75%,#f8fafc)] bg-[length:40px_40px] bg-[position:0_0,20px_20px] opacity-30" />
            </div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Découvrez nos expertises
              </h2>
              <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto px-4 leading-relaxed">
                Des solutions <span className="text-blue-600 font-medium">complètes</span> et <span className="text-purple-600 font-medium">sur mesure</span> pour répondre à tous vos besoins en matière de <span className="text-indigo-600 font-medium">sécurité informatique</span> et de <span className="text-blue-600 font-medium">transformation numérique</span>.
              </p>
            </div>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 gap-12">
            {servicesData.map((service) => (
              <motion.div
                key={service.id}
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
                id={service.id}
              >
                <ServiceCard
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  features={service.features}
                  imageSrc={service.imageSrc}
                  className="h-full"
                  imageFirst={service.id === 'telecommunications' || service.id === 'maintenance'}
                />
              </motion.div>
            ))}
          </div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-20"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              Besoin d'une solution personnalisée ?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Nos experts sont à votre disposition pour étudier vos besoins et vous proposer une solution adaptée à votre entreprise.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors shadow-lg"
            >
              Contactez-nous
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </a>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Services;












